<template>
  <div class="child-route-wrapper">
    <h1 class="registration-form-title">
      Verify your email address
    </h1>
    <p class="registration-form-subtitle">
      We just sent a 6-digit verification code to <br>{{ email }}
    </p>

    <div class="registration-form-wrapper">
      <div class="otp-section">
        <div class="otp-header">
          Enter code
        </div>
        <div class="otp-inputs">
          <b-form-input
            v-for="(digit, index) in otpDigits"
            :key="`otp-${index}`"
            v-model="otpDigits[index]"
            maxlength="1"
            inputmode="numeric"
            class="otp-digit form-control"
            :class="{ 'is-invalid-otp': hasError }"
            :data-otp-index="index"
            @input="handleInput(index)"
            @paste="index === 0 && handlePaste($event)"
            @keypress="onlyAllowPositiveDecimal"
            @keydown="handleKeydown($event, index)"
            @focus="handleFocus"
            @blur="handleBlur"
          />
        </div>
        <div
          v-if="hasError"
          class="error-message"
        >
          <small class="text-danger">{{ errorMessage }}</small>
        </div>
      </div>

      <div class="resend-section text-left">
        <span>Didn't get the code? </span>
        <template v-if="timerActive">
          <span class="resend-timer">Resend code {{ formattedTimer }}</span>
        </template>
        <b-link
          v-else
          href="#"
          class="resend-link"
          @click.prevent="resendCode"
        >
          Resend code
        </b-link>
      </div>

      <b-button
        class="registration-button"
        :disabled="!canSubmit || isVerifying"
        @click="verifyOtpCode"
      >
        <b-spinner
          v-if="isVerifying"
          small
        />
        <span v-else>Confirm</span>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  BFormInput, BButton, BLink, BSpinner,
} from 'bootstrap-vue'
import { useFormFields } from '@/views/apps/authentication-v2/useFormFields'
import { REQUEST_OTP, VERIFY_OTP } from '@/store/modules/register.module'
import { apiToastSuccess, apiToastError } from '@/@core/utils/toast'
import { useOtpTimer } from '@/views/apps/authentication-v2/child-routes/composables/useOtpTimer'
import { useOtp } from '@/views/apps/authentication-v2/child-routes/composables/useOtp'
import { useOtpErrors } from '@/views/apps/authentication-v2/child-routes/composables/useOtpErrors'
import { AUTH_REGISTER_V2_CHILDREN } from '@/router/routes/utils'
import { onlyAllowPositiveDecimal } from '@/views/apps/product/edit/utils'
import analytics from '@core/utils/analytics'
import constants from '@/constants'
import { throttle } from 'lodash'

export default {
  name: 'SetOtp',
  components: {
    BFormInput,
    BButton,
    BLink,
    BSpinner,
  },
  mounted() {
    // Track OTP view event
    analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.OTP_VIEW, {
      pageName: 'OTP View Page',
    })
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const { email } = useFormFields()

    // OTP input management
    const {
      otpDigits,
      isOtpComplete,
      isInteracting,
      handleInput,
      handleKeydown,
      handlePaste,
      handleFocus,
      handleBlur,
      resetOtp,
    } = useOtp()

    // Error & validation management
    const {
      isValid,
      hasError,
      errorMessage,
      validate,
      clearAllErrors,
      setApiError,
    } = useOtpErrors({ isOtpComplete, isInteracting, otpDigits })

    // Validation Flow
    watch(isInteracting, (interacting, prevInteracting) => {
      if (interacting && !prevInteracting) clearAllErrors()
      if (!interacting && prevInteracting) validate()
    })

    watch(isOtpComplete, isComplete => {
      if (isComplete && !isInteracting.value) validate()
    })

    // Timer
    const {
      timerActive,
      formattedTimer,
      startTimer,
      resendCode: originalResendCode,
    } = useOtpTimer({
      onResend: async () => {
        try {
          await proxy.$store.dispatch(REQUEST_OTP, { email: email.value })
          resetOtp()
          clearAllErrors()
          apiToastSuccess('Verification code resent')
        } catch (e) {
          setApiError(e.response?.data?.message || 'Failed to send code')
          apiToastError(errorMessage.value, 'TimesIcon')
        }
      },
    })

    // Throttle with leading edge only
    const resendCode = throttle(async () => {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.OTP_RESEND)
      await originalResendCode()
    }, 5000, { leading: true, trailing: false })

    const isVerifying = ref(false)
    const canSubmit = computed(() => isValid.value)

    async function verifyOtpCode() {
      if (!validate()) return

      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.OTP_VERIFICATION_ATTEMPT, {
        email: email.value,
        otp: otpDigits.value.join(''),
      })

      isVerifying.value = true

      try {
        await submitOtp()
        handleSuccess()
      } catch (error) {
        handleError(error)
      } finally {
        isVerifying.value = false
      }
    }

    async function submitOtp() {
      return proxy.$store.dispatch(VERIFY_OTP, {
        email: email.value,
        code: otpDigits.value.join(''),
      })
    }

    function handleSuccess() {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.OTP_VERIFICATION_SUCCESS)
      apiToastSuccess('Email verified successfully')
      proxy.$router.push({ name: AUTH_REGISTER_V2_CHILDREN.COMPLETE_INFO })
    }

    function handleError(error) {
      const message = error?.response?.data?.message || 'Error verifying OTP'

      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.OTP_VERIFICATION_FAILED, {
        email: email.value,
        otp: otpDigits.value.join(''),
        error: message,
      })

      setApiError(message)
      apiToastError(errorMessage.value, 'TimesIcon')
    }

    startTimer()

    return {
      email,
      otpDigits,
      handleInput,
      handleKeydown,
      handlePaste,
      handleFocus,
      handleBlur,
      resetOtp,
      hasError,
      errorMessage,
      timerActive,
      formattedTimer,
      resendCode,
      canSubmit,
      isVerifying,
      verifyOtpCode,
      onlyAllowPositiveDecimal,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../common-styles.scss';

.registration-form-title {
  margin-bottom: 12px;
}

.registration-form-subtitle {
  text-align: center;
  margin-bottom: 40px;
  font-size: 16px;
  line-height: 24px;
  color: #6c757d;
}

.registration-form-wrapper {
  width: 100%;
  max-width: 528px;
  margin: 0 auto;
}

.otp-section {
  width: 100%;
}

.otp-header {
  font-size: 16px;
  margin-bottom: 8px;
}

.otp-inputs {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.otp-digit {
  width: 44px;
  height: 46px;
  font-size: 20px;
  text-align: center;
  border-radius: 8px;
  padding: 8px;

  &:last-child {
    margin-right: 0;
  }

  &.is-invalid-otp {
    border: 1px solid #ea5455;
  }
}

.resend-section {
  text-align: center;
  margin-bottom: 20px;
  font-size: 16px;
}

.resend-link {
  color: #0368A0;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.resend-timer {
  color: #6c757d;
}

.error-message {
  color: #ea5455;
  font-size: 12px;
  margin-bottom: 16px;
}

/* Override default Bootstrap spinner positioning */
.registration-button .spinner-border {
  vertical-align: middle;
}
</style>
